import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Star, ExternalLink } from 'lucide-react';

// Icônes sociales en SVG inline (compatible toutes versions lucide)
const InstagramIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const FacebookIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
  </svg>
);

const Footer = () => {
  return (
    <footer className="bg-[#332724] text-[#FDFAF4] py-16">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img
              src="/assets/logo-harmony.jpg"
              alt="Harmony Parfumerie"
              className="h-16 w-16 rounded-full object-cover"
            />
            <div>
              <h3
                className="text-2xl text-[#B8922A] leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Harmony
              </h3>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#1E7A6A]">
                Bar à parfums · Concept store
              </p>
            </div>
          </div>
          <p className="text-sm font-light leading-relaxed mb-4">
            Votre bar à parfums au Polygone de Béziers. Fragrances d'exception, personnalisation
            sur-mesure et engagement éco-responsable avec des parfums rechargeables.
          </p>

          {/* Social Media */}
          <div className="flex gap-3 mt-4">
            <a
              href="https://www.instagram.com/harmony.maison/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-[#FDFAF4]/10 hover:bg-[#B8922A] rounded-full flex items-center justify-center transition-colors"
              data-testid="social-instagram"
              aria-label="Instagram"
            >
              <InstagramIcon size={18} />
            </a>
            <a
              href="https://www.facebook.com/PolygoneBeziers/posts/ouverture-dun-bar-%C3%A0-parfum-au-polygone-maison-harmony-propose-une-large-gamme-de/810162764705551/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-[#FDFAF4]/10 hover:bg-[#B8922A] rounded-full flex items-center justify-center transition-colors"
              data-testid="social-facebook"
              aria-label="Facebook"
            >
              <FacebookIcon size={18} />
            </a>
            <a
              href="https://www.tiktok.com/@harmonymaison"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-[#FDFAF4]/10 hover:bg-[#B8922A] rounded-full flex items-center justify-center transition-colors"
              data-testid="social-tiktok"
              aria-label="TikTok"
            >
              <TikTokIcon size={18} />
            </a>
          </div>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-lg mb-4 font-medium uppercase tracking-[0.2em]">Contact</h4>
          <div className="space-y-3 text-sm font-light">
            <div className="flex items-start gap-2" data-testid="footer-address">
              <MapPin size={18} className="text-[#1E7A6A] mt-1 flex-shrink-0" />
              <span>
                3 Carrefour de l'Hours, Niveau Écluses
                <br />
                Polygone de Béziers
              </span>
            </div>
            <a
              href="tel:0467215708"
              className="flex items-center gap-2 hover:text-[#B8922A] transition-colors"
              data-testid="footer-phone"
            >
              <Phone size={18} className="text-[#1E7A6A]" />
              <span>04 67 21 57 08</span>
            </a>
            <a
              href="mailto:contact@maisonharmony.com"
              className="flex items-center gap-2 hover:text-[#B8922A] transition-colors"
              data-testid="footer-email"
            >
              <Mail size={18} className="text-[#1E7A6A]" />
              <span>contact@maisonharmony.com</span>
            </a>
          </div>
        </div>

        {/* Horaires */}
        <div>
          <h4 className="text-lg mb-4 font-medium uppercase tracking-[0.2em]">Horaires</h4>
          <div className="space-y-2 text-sm font-light">
            <p>Lundi - Samedi: 10h - 20h</p>
            <p className="text-[#FDFAF4]/60">Fermé le dimanche</p>
          </div>
        </div>

        {/* Liens utiles */}
        <div>
          <h4 className="text-lg mb-4 font-medium uppercase tracking-[0.2em]">Liens utiles</h4>
          <div className="space-y-2 text-sm font-light">
            <Link to="/qui-sommes-nous" className="block hover:text-[#B8922A] transition-colors">
              Qui sommes-nous
            </Link>
            <Link to="/boutique" className="block hover:text-[#B8922A] transition-colors">
              Offrir un bon cadeau
            </Link>
            <Link to="/boutique/abonnement" className="block hover:text-[#B8922A] transition-colors">
              Abonnement mensuel
            </Link>
            <Link to="/fidelite" className="block hover:text-[#B8922A] transition-colors font-medium" data-testid="footer-fidelite-link">
              ✨ Cercle Harmony (fidélité)
            </Link>
            <Link to="/univers" className="block hover:text-[#B8922A] transition-colors">
              ✨ L&apos;Univers Harmony (services)
            </Link>
            <Link to="/livraison" className="block hover:text-[#B8922A] transition-colors">
              Livraison & retrait
            </Link>
            <Link to="/avis" className="block hover:text-[#B8922A] transition-colors">
              Avis clients
            </Link>
            <Link to="/faq" className="block hover:text-[#B8922A] transition-colors">
              FAQ
            </Link>
            <a
              href="https://www.google.com/search?sca_esv=25795b5d4f09c1a9&q=avis+sur+maison+harmony+-+parfumerie+b%C3%A9ziers&uds=ALYpb_ncDc7jTlmw6Mmq7NjuX5c-YspAyyO1LAGnz_sZMslwz50Km9V0V_Ljjdb1cJYBG9ScWZ846YLPEirq1yjRc81MNciiXo-_p9ZqpdP8xG_AUgCAS_l-ePOZYuxJWfWIp1sjnkEAWmTqaTu0G6i5K245UzFeS674C8GRGi0RvDkQu9xbFAGwM6j3wyK_MwpsNb6xtEWpTQYd7Z5KQrWoZ84HUjoyaydzyLoxdsNOv2DeigRuejxA1sEfwNV94E6C-zNI9Tq01lRhoryywDkegRsHzux5cKOxHK41e0Su19BMpwcdp24qA6EFSLezEdnPL6f2y9FmgKFvqNv_l07kGQeUzCzlpffHy_aD_2R5CPaZXeT20sAdaXaNFwXVrFtzVcbetfp3oRFzd2fArb0PqE_vM4LJlQbJ-onancmM-qPKDs1_hYI"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-[#B8922A] transition-colors"
              data-testid="google-reviews-link"
            >
              <Star size={14} />
              <span>Avis Google</span>
              <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-12 pt-8 border-t border-[#FDFAF4]/20 text-center text-sm font-light">
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-4 text-xs uppercase tracking-[0.2em]">
          <Link to="/mentions-legales" className="hover:text-[#B8922A] transition-colors" data-testid="footer-mentions">
            Mentions légales
          </Link>
          <span>·</span>
          <Link to="/cgv" className="hover:text-[#B8922A] transition-colors" data-testid="footer-cgv">
            CGV
          </Link>
          <span>·</span>
          <Link to="/confidentialite" className="hover:text-[#B8922A] transition-colors" data-testid="footer-confidentialite">
            Politique de confidentialité
          </Link>
          <Link to="/faq" className="hover:text-[#B8922A] transition-colors">
            FAQ
          </Link>
        </div>
        <p>&copy; 2024 Harmony Parfumerie — MAISON HARMONY SASU · RCS Montpellier B 928 654 946 · Tous droits réservés.</p>
      </div>
    </footer>
  );
};

export default Footer;
